import { useMemo } from 'react';
import type { TableInfo } from '@/types/api';
import { formatDistanceToNow } from 'date-fns';
import { TABLE_STATUS_CONFIG } from '@/components/tables/tableStatusConfig';
import { argbIntToHex, contrastText } from '@/utils/color';

interface TableGridProps {
  tables: TableInfo[];
  onTableClick?: (table: TableInfo) => void;
}

const statusConfig = TABLE_STATUS_CONFIG;

export default function TableGrid({ tables, onTableClick }: TableGridProps) {
  // Group tables by section, with Unassigned always last
  const tablesBySection = useMemo(() => {
    const sections = new Map<string, TableInfo[]>();

    tables.forEach((table) => {
      const section = table.section || 'Unassigned';
      if (!sections.has(section)) {
        sections.set(section, []);
      }
      sections.get(section)!.push(table);
    });

    // Sort tables by table number within each section
    sections.forEach((sectionTables) => {
      sectionTables.sort((a, b) =>
        a.tableNumber.localeCompare(b.tableNumber, undefined, { numeric: true })
      );
    });

    // Re-order: floors first (sorted), Unassigned last
    const ordered = new Map<string, TableInfo[]>();
    const keys = Array.from(sections.keys()).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    keys.forEach((key) => ordered.set(key, sections.get(key)!));

    return ordered;
  }, [tables]);

  const stats = useMemo(() => {
    const total = tables.length;
    const available = tables.filter((t) => t.status === 'AVAILABLE').length;
    const occupied = tables.filter((t) => t.status === 'OCCUPIED').length;
    const reserved = tables.filter((t) => t.status === 'RESERVED').length;

    return { total, available, occupied, reserved };
  }, [tables]);

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-sm text-gray-600">Total Tables</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Available</div>
          <div className="text-2xl font-bold text-green-600">
            {stats.available}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Occupied</div>
          <div className="text-2xl font-bold text-red-600">{stats.occupied}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-600">Reserved</div>
          <div className="text-2xl font-bold text-blue-600">
            {stats.reserved}
          </div>
        </div>
      </div>

      {/* Tables by Section */}
      {Array.from(tablesBySection.entries()).map(([section, sectionTables]) => (
        <div key={section} className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {section === 'Unassigned' ? 'Unassigned' : `${section} Floor`}
            <span className="ml-2 text-sm text-gray-500">
              ({sectionTables.length} tables)
            </span>
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {sectionTables.map((table) => {
              const config = statusConfig[table.status];
              // When the table has an exact POS TableStatus color, the whole card
              // follows that color (with contrast-aware text); otherwise fall back
              // to the coarse flag-based palette.
              const statusColor = argbIntToHex(table.statusColorRgb);
              const textColor = contrastText(statusColor);
              const colored = !!statusColor;
              const subTextClass = colored ? 'opacity-80' : 'text-gray-600';

              return (
                <button
                  key={table.id}
                  onClick={() => onTableClick?.(table)}
                  className={`card p-4 border-2 transition-all hover:shadow-md ${colored ? '' : `${config.bgColor} ${config.color}`}`}
                  style={
                    colored
                      ? { backgroundColor: statusColor!, borderColor: `${textColor}33`, color: textColor! }
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-bold">
                      {table.tableNumber}
                    </span>
                    <span className="text-xl">{config.emoji}</span>
                  </div>

                  <div className="text-xs space-y-1">
                    <div className="font-medium">{config.label}</div>

                    {table.statusName && (
                      <span
                        className="mt-1 inline-block rounded border px-1.5 py-0.5 text-xs font-medium"
                        style={colored ? { borderColor: textColor!, color: textColor! } : undefined}
                      >
                        {table.statusName}
                      </span>
                    )}
                    <div className={subTextClass}>
                      Capacity: {table.capacity}
                    </div>

                    {table.status === 'OCCUPIED' && table.covers && (
                      <div className="font-medium">
                        Covers: {table.covers}
                      </div>
                    )}

                    {table.status === 'OCCUPIED' && table.timeSeated && (
                      <div className={subTextClass}>
                        {formatDistanceToNow(new Date(table.timeSeated), {
                          addSuffix: true,
                        })}
                      </div>
                    )}

                    {table.serverName && (
                      <div className={`${subTextClass} truncate`}>
                        {table.serverName}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {tables.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-gray-500">No tables found</p>
        </div>
      )}
    </div>
  );
}
